import { AxiosError } from 'axios';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import {
  IFieldErrors,
  IWorkflowFormSubmission,
  IWorkflowFormSubmissionCommentData,
  IWorkflowFormTemplateRevision,
  IWorkflowState,
  IWorkflowTransition,
  SystemError,
  WorkflowStateAssignmentTypeEnum
} from '@/models';
import { ISystemError } from '@/models/error';
import { IUpdateWorkflowFormSubmissionStatePayload } from '@/services';
import { IRootState } from '@/store';
import {
  IWorkflowFormActions,
  IWorkflowFormGetters,
  IWorkflowFormMutations,
  IWorkflowFormState
} from '@/store/workflow-form';
import { isHtmlTextEmpty, setErrorFromAxiosError } from '@/utils/helpers';
import {
  defineComponent,
  h,
  nextTick,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { useAuth } from '@/composable/auth';

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import WorkflowFormStateAvailableAssigneePicker from './WorkflowFormStateAvailableAssigneePicker.vue';
import WorkflowFormSubmissionCommentField from './WorkflowFormSubmissionCommentField.vue';

const WorkflowFormSubmissionStateActions = defineComponent({
  name: 'WorkflowFormSubmissionStateActions',

  props: {
    submission: {
      type: Object as PropType<IWorkflowFormSubmission>,
      required: true
    }
  },

  emits: {
    'add-transition-comment': () => true,
    'refresh-comments': () => true
  },

  setup(props, { emit }) {
    const isTransitionsLoading = ref(false);
    const isStateLoading = ref(false);

    const fetchError = ref<ISystemError | null>(null);
    const transitionError = ref<ISystemError | null>(null);

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowFormState,
      IWorkflowFormGetters,
      IWorkflowFormActions,
      IWorkflowFormMutations
    >(store, 'workflowForm');

    const {
      updateWorkflowFormSubmissionState,
      fetchWorkflowFormSubmissionValidStateTransitions,
      fetchWorkflowFormSubmissionsAvailableAssignees
    } = useActions([
      'updateWorkflowFormSubmissionState',
      'fetchWorkflowFormSubmissionValidStateTransitions',
      'fetchWorkflowFormSubmissionsAvailableAssignees'
    ]);

    const { useActions: useSnackbarActions } = createNamespacedHelpers(
      store,
      'snackbar'
    );
    const { createSnack } = useSnackbarActions(['createSnack']);

    const {
      getWorkflowFormSubmissionTransitions: getConnectedTransitions
    } = useGetters(['getWorkflowFormSubmissionTransitions']);

    watch(
      () => props.submission,
      () => {
        getAvailableTransitions();
      },
      {
        immediate: true
      }
    );

    function clearAllErrors() {
      fetchError.value = null;
      transitionError.value = null;
    }

    function getAvailableTransitions() {
      clearAllErrors();
      isTransitionsLoading.value = true;

      return fetchWorkflowFormSubmissionValidStateTransitions(
        props.submission.id!
      )
        .catch(error => {
          fetchError.value = setErrorFromAxiosError(error);
        })
        .finally(() => {
          isTransitionsLoading.value = false;
        });
    }

    async function updateSubmissionFormState(
      state: number,
      assignee?: number,
      comment?: string
    ) {
      clearAllErrors();

      const payload = {
        id: props.submission.id,
        state: state,
        assignee: assignee,
        comment: comment
      } as IUpdateWorkflowFormSubmissionStatePayload;

      await updateWorkflowFormSubmissionState(payload);

      if (comment === undefined) {
        emit('add-transition-comment');
      } else {
        emit('refresh-comments');
      }
      getAvailableTransitions();

      createSnack({
        color: props.submission.state?.config.color,
        type: props.submission.state?.config.color,
        right: true,
        useBtn: false,
        msg: `Workflow Form Submission: ${props.submission.state?.name}`,
        btnText: 'Ok'
      });
    }

    function loadAvailableAssignees(stateId: number) {
      clearAllErrors();
      return fetchWorkflowFormSubmissionsAvailableAssignees({
        submissionId: props.submission.id!,
        stateId: stateId
      });
    }

    async function handleStateTransition(
      transition: IWorkflowTransition,
      targetState: IWorkflowState,
      onClick: (event: MouseEvent) => void,
      event: MouseEvent
    ) {
      try {
        isStateLoading.value = true;

        if (
          targetState.assignment_type ===
          WorkflowStateAssignmentTypeEnum.PermissionGroup
        ) {
          await loadAvailableAssignees(targetState.id!);
          return onClick(event);
        }
        if (transition.comment_required) {
          const comment = await openCommentDialog(transition);
          if (comment) {
            await updateSubmissionFormState(
              targetState.id!,
              undefined,
              comment
            );
          }
          return;
        }
        return await updateSubmissionFormState(targetState.id!);
      } catch (err) {
        transitionError.value = setErrorFromAxiosError(err as AxiosError);
        createSnack({
          color: 'error',
          type: 'error',
          right: true,
          useBtn: false,
          msg: `Failed to update ${
            (props.submission.form as IWorkflowFormTemplateRevision).template!
              .title
          } submission state: ${transitionError.value.message}`,
          btnText: 'Ok'
        });
      } finally {
        isStateLoading.value = false;
      }
    }

    const assignee = ref<number | null>(null);

    const displayFormErrors = ref(false);

    const isCommentDialogActive = ref(false);
    const mandatoryCommentData = ref<IWorkflowFormSubmissionCommentData>({
      text: ''
    });
    const commentDialogResolve = ref<((value: string) => void) | null>(null);
    const pendingCommentTransition = ref<IWorkflowTransition | null>(null);

    function openCommentDialog(
      transition: IWorkflowTransition
    ): Promise<string> {
      pendingCommentTransition.value = transition;
      mandatoryCommentData.value = { text: '' };
      isCommentDialogActive.value = true;
      return nextTick().then(
        () =>
          new Promise<string>(resolve => {
            commentDialogResolve.value = resolve;
          })
      );
    }

    function confirmCommentDialog() {
      const text = mandatoryCommentData.value?.text ?? '';
      if (
        pendingCommentTransition.value?.comment_required &&
        isHtmlTextEmpty(text)
      ) {
        return;
      }
      if (commentDialogResolve.value) {
        commentDialogResolve.value(text);
      }
      isCommentDialogActive.value = false;
      pendingCommentTransition.value = null;
      commentDialogResolve.value = null;
    }

    function cancelCommentDialog() {
      if (commentDialogResolve.value) {
        commentDialogResolve.value('');
      }
      isCommentDialogActive.value = false;
      pendingCommentTransition.value = null;
      commentDialogResolve.value = null;
    }

    const assigneeDialogCommentData = ref<IWorkflowFormSubmissionCommentData>({
      text: ''
    });

    function genAssigneeStateAction(
      transition: IWorkflowTransition,
      type: 'button' | 'list-item' = 'button'
    ) {
      const targetState = transition.target_state as IWorkflowState;
      const isConfirmationDialogActive = ref(false);

      return h(ConfirmationDialog, {
        props: {
          errorMessages: transitionError.value?.message,
          value: isConfirmationDialogActive.value
        },
        on: {
          input: (value: boolean) => {
            isConfirmationDialogActive.value = value;
            if (!value) {
              displayFormErrors.value = false;
              assigneeDialogCommentData.value = { text: '' };
            }
          },
          clearAllErrors: () => {
            clearAllErrors();
          }
        },
        attrs: {
          persistent: true,
          title: 'Select Assignee',
          message: 'Select an user to assign'
        },

        scopedSlots: {
          content: () => [
            h(WorkflowFormStateAvailableAssigneePicker, {
              class: 'my-1',
              props: {
                submissionId: props.submission.id,
                stateId: targetState.id,
                value: assignee.value,
                errorMessages: transitionError.value?.detail
              },
              attrs: {
                placeholder: 'Select an user to assign',
                disabled: isStateLoading.value
              },
              on: {
                input: (value: number) => {
                  clearAllErrors();
                  assignee.value = value;
                },
                'assignee-list-empty': () => {
                  transitionError.value = new SystemError({
                    name: 'SystemError',
                    message: 'Empty Assignee List',
                    detail:
                      'No assignees found. Check Configuration with Form Admin '
                  });
                }
              }
            }),

            ...(transition.comment_required
              ? [
                  h(WorkflowFormSubmissionCommentField, {
                    class: 'rounded my-1',
                    props: {
                      submissionId: props.submission.id!,
                      value: assigneeDialogCommentData.value
                    },
                    on: {
                      input: (v: IWorkflowFormSubmissionCommentData) => {
                        assigneeDialogCommentData.value = v;
                      }
                    }
                  })
                ]
              : []),

            transitionError.value &&
            transitionError.value.fieldErrors &&
            transitionError.value.fieldErrors?.answers
              ? h(
                  'v-card',
                  {
                    class:
                      'v-messages__message caption text-capitalize d-flex flex-row align-center rounded-lg my-1 ml-3 px-1',
                    props: {
                      width: 'fit-content',
                      flat: true,
                      text: true,
                      xSmall: true,
                      height: '20px'
                    },
                    on: {
                      click: () => {
                        displayFormErrors.value = !displayFormErrors.value;
                      }
                    }
                  },
                  [
                    h(
                      'span',
                      { class: 'error--text error-text-link' },
                      `${displayFormErrors.value ? 'Hide' : 'View'} Errors`
                    ),
                    h(
                      'v-icon',
                      {
                        class: 'ml-1',
                        props: {
                          color: 'error',
                          right: true,
                          size: 16
                        }
                      },
                      [
                        displayFormErrors.value
                          ? 'mdi-chevron-up'
                          : 'mdi-chevron-down'
                      ]
                    ),
                    h('div', {
                      class: 'card-underlay error',
                      style: {
                        opacity: 0.18
                      }
                    })
                  ]
                )
              : '',
            transitionError.value?.fieldErrors?.answers &&
            displayFormErrors.value
              ? h(
                  'div',
                  {
                    class: 'v-messages theme--light error--text pl-3',
                    attrs: {
                      role: 'alert'
                    }
                  },
                  [
                    h(
                      'div',
                      {
                        class: 'v-messages__wrapper'
                      },
                      [
                        h(
                          'div',
                          {
                            class: 'v-messages__message'
                          },
                          [
                            Array.isArray(
                              transitionError.value!.fieldErrors.answers
                            )
                              ? h(
                                  'ul',
                                  {
                                    style: { margin: 0, paddingLeft: '1.2em' }
                                  },
                                  transitionError
                                    .value!.fieldErrors.answers.filter(
                                      errorObj =>
                                        errorObj &&
                                        !!((errorObj as unknown) as IFieldErrors)
                                          .detail
                                    )
                                    .map((error, index) =>
                                      h(
                                        'li',
                                        {
                                          key: index,
                                          style: { marginBottom: '4px' }
                                        },
                                        (error as any).detail
                                      )
                                    )
                                )
                              : ''
                          ]
                        )
                      ]
                    )
                  ]
                )
              : ''
          ],
          activator: ({ on, attrs }) => {
            return type === 'button'
              ? h(
                  'VBtn',
                  {
                    on: {
                      click: async (event: MouseEvent) =>
                        handleStateTransition(
                          transition,
                          targetState,
                          on.click,
                          event
                        )
                    },
                    attrs: {
                      ...attrs,
                      dark: true,
                      depressed: true,
                      color: targetState.config.color
                    },
                    class: 'text-capitalize'
                  },
                  [transition.name]
                )
              : h(
                  'v-list-item',
                  {
                    class: 'text-capitalize rounded-0',
                    attrs: {
                      ...attrs,
                      color: targetState.config.color
                    },
                    on: {
                      click: async (event: MouseEvent) =>
                        handleStateTransition(
                          transition,
                          targetState,
                          on.click,
                          event
                        )
                    }
                  },
                  [
                    h('v-card', {
                      class: 'position-absolute',
                      style: {
                        left: 0
                      },
                      attrs: {
                        color: targetState.config.color,
                        width: 2,
                        height: '100%',
                        tile: true,
                        flat: true
                      }
                    }),
                    h('v-list-item-content', { class: 'pl-1' }, [
                      h('v-list-item-title', transition.name)
                    ])
                  ]
                );
          },
          actions: () => [
            h('v-spacer'),
            h(
              'v-btn',
              {
                class: 'text-capitalize',
                attrs: {
                  color: 'primary',
                  depressed: true,
                  loading: isStateLoading.value,
                  disabled:
                    !assignee.value ||
                    isStateLoading.value ||
                    (transition.comment_required &&
                      isHtmlTextEmpty(assigneeDialogCommentData.value?.text))
                },
                on: {
                  click: async (_event: MouseEvent) => {
                    try {
                      isStateLoading.value = true;
                      await updateSubmissionFormState(
                        targetState.id!,
                        assignee.value!,
                        transition.comment_required
                          ? assigneeDialogCommentData.value?.text
                          : undefined
                      );
                      assignee.value = null;
                      assigneeDialogCommentData.value = { text: '' };
                      isConfirmationDialogActive.value = false;
                    } catch (err) {
                      transitionError.value = setErrorFromAxiosError(
                        err as AxiosError
                      );
                    } finally {
                      isStateLoading.value = false;
                    }
                  }
                }
              },
              [transition.name]
            )
          ]
        }
      });
    }

    const { currentUser } = useAuth();

    return () => {
      if (
        props.submission.assignee?.id !== currentUser.value.id &&
        !currentUser.value.hasRoles(['Form Admin'])
      ) {
        return null;
      }

      if (fetchError.value != null) {
        return h(ErrorAlert, {
          class: 'mb-0 rounded',
          props: {
            errorMessage: fetchError.value.message,
            detailErrorMessage: fetchError.value.detail
          },
          on: {
            clearErrors: () => {
              getAvailableTransitions();
            }
          }
        });
      }

      const transitionContent = h(
        'v-fade-transition',
        {
          attrs: {
            mode: 'out-in'
          }
        },
        [
          isTransitionsLoading.value
            ? h('v-progress-circular', {
                attrs: {
                  indeterminate: true,
                  width: 3,
                  size: 30,
                  color: 'primary'
                }
              })
            : getConnectedTransitions.value.length > 1
            ? h('v-menu', {
                attrs: {
                  offsetY: true
                },
                scopedSlots: {
                  activator: ({ on, attrs }) =>
                    h(
                      'v-btn',
                      {
                        class: 'text-capitalize',
                        attrs: {
                          color: 'primary',
                          depressed: true,
                          outlined: true,
                          loading: isStateLoading.value,
                          ...attrs
                        },
                        on: on
                      },
                      [
                        'Actions',
                        h(
                          'v-icon',
                          {
                            class: 'ml-1',
                            attrs: {
                              right: true
                            }
                          },
                          'mdi-menu-down'
                        )
                      ]
                    ),
                  default: () =>
                    h(
                      'v-list',
                      {
                        class: 'px-0',
                        attrs: {
                          nav: true,
                          dense: true
                        }
                      },
                      getConnectedTransitions.value.map(transition =>
                        genAssigneeStateAction(transition, 'list-item')
                      )
                    )
                }
              })
            : getConnectedTransitions.value.map(transition =>
                genAssigneeStateAction(transition, 'button')
              )
        ]
      );

      const commentDialog = h(ConfirmationDialog, {
        props: {
          value: isCommentDialogActive.value,
          title: 'Comment required',
          message:
            pendingCommentTransition.value != null
              ? `Please provide a comment for transition: ${pendingCommentTransition.value.name}`
              : 'Please provide a comment for this transition',
          persistent: true,
          width: 600
        },
        on: {
          input: (value: boolean) => {
            isCommentDialogActive.value = value;
            if (!value) {
              cancelCommentDialog();
            }
          }
        },
        scopedSlots: {
          content: () => [
            h(WorkflowFormSubmissionCommentField, {
              class: 'rounded my-1',
              props: {
                submissionId: props.submission.id!,
                value: mandatoryCommentData.value
              },
              on: {
                input: (v: IWorkflowFormSubmissionCommentData) => {
                  mandatoryCommentData.value = v;
                }
              }
            })
          ],
          actions: () => [
            h('v-spacer'),
            h(
              'v-btn',
              {
                class: 'text-capitalize',
                props: { text: true, color: 'error', depressed: true },
                on: { click: cancelCommentDialog }
              },
              'Cancel'
            ),
            h(
              'v-btn',
              {
                class: 'text-capitalize ml-2',
                props: {
                  color: 'primary',
                  depressed: true,
                  disabled:
                    pendingCommentTransition.value?.comment_required &&
                    isHtmlTextEmpty(mandatoryCommentData.value?.text)
                },
                on: { click: confirmCommentDialog }
              },
              'Confirm'
            )
          ]
        }
      });

      return h('div', [transitionContent, commentDialog]);
    };
  }
});

export default WorkflowFormSubmissionStateActions;
